import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, shortSku, shortWax, fmtDate, getOperator, setOperator } from './wickTestApi';
import './WickTesting.css';

/**
 * Pour station — Joleen's list. Public (production kiosk), no login.
 *
 *   /production/wick-pour
 *
 * Every queued test shows the recipe (vessel, oil, wax) and the candidate
 * candles to pour, each with its label code. Print the sheet, pour, stick the
 * labels on, tap "Poured" and the candles move to the lab queue.
 */
export default function WickTestPourSheet() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [by, setBy] = useState(getOperator());
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.pourSheet().then(setData).catch((e) => setError(e.response?.data?.error || e.message));
  }, []);
  useEffect(() => { load(); }, [load]);

  const markPoured = async (r) => {
    if (!by.trim()) { window.alert('Put your initials in first.'); return; }
    setBusy(r.id);
    try { await api.markPoured(r.id, by.trim()); load(); } catch (e) { window.alert(e.response?.data?.error || e.message); } finally { setBusy(null); }
  };

  const rows = data?.rows || [];
  const totalCandles = rows.reduce((n, r) => n + (r.candidates || []).length, 0);

  return (
    <div className="wt-app">
      <div className="wt-header wt-no-print">
        <div>
          <h1>Wick tests to pour</h1>
          <p className="wt-sub">{rows.length} test{rows.length === 1 ? '' : 's'} · {totalCandles} candles · wax {data ? shortWax(data.current_wax_sku) : ''}</p>
        </div>
        <div className="wt-toolbar">
          <label className="wt-note">Poured by <input className="wt-input" value={by} onChange={(e) => { setBy(e.target.value); setOperator(e.target.value); }} placeholder="initials" style={{ width: 90 }} /></label>
          <Link className="wt-btn secondary" to={`/production/wick-test-labels?ids=${rows.map((r) => r.id).join(',')}`} target="_blank">Print all labels</Link>
          <button className="wt-btn secondary" onClick={() => window.print()}>Print sheet</button>
          <button className="wt-btn secondary" onClick={load}>Refresh</button>
        </div>
      </div>

      <div className="wt-print-only">
        <h1>Wick tests to pour — {fmtDate(new Date().toISOString())}</h1>
      </div>

      {error && <div className="wt-error">{error}</div>}
      {data && !rows.length && <div className="wt-note" style={{ padding: 20 }}>Nothing to pour right now.</div>}

      {rows.map((r) => (
        <div className="wt-pour-request" key={r.id}>
          <div className="head">
            <span className="title">{shortSku(r.oil_sku)} · {r.size_label || shortSku(r.vessel_sku)}</span>
            <span className="code">{r.code}</span>
            {r.priority > 0 && <span className="wt-badge warn">Priority</span>}
          </div>
          <div className="recipe">
            <strong>Vessel:</strong> {r.vessel_sku} &nbsp; <strong>Oil:</strong> {r.oil_sku} &nbsp; <strong>Wax:</strong> {r.wax_sku}
            {r.product_skus?.length ? <> &nbsp; <span className="wt-muted">({r.product_skus.join(', ')})</span></> : null}
          </div>
          {r.notes && <div className="recipe"><strong>Notes:</strong> {r.notes}</div>}
          <div className="wt-pour-cands">
            {(r.candidates || []).map((c) => (
              <div className="wt-pour-cand" key={c.id}>
                <span className="wt-checkbox" />
                <span className="code">{c.code}</span> — {c.wick_label}
                <div className="sku">{c.wick_sku || 'not a stocked wick — cut to length'}</div>
              </div>
            ))}
          </div>
          <div className="wt-card-actions wt-no-print">
            <Link className="wt-btn small secondary" to={`/production/wick-test-labels?ids=${r.id}`} target="_blank">Labels</Link>
            <button className="wt-btn small" disabled={busy === r.id} onClick={() => markPoured(r)}>Poured — send to lab</button>
          </div>
        </div>
      ))}
    </div>
  );
}
